import { useTranslation } from "react-i18next";
import { Collapse, FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import { useStyle } from "./style";
import { IconInfos } from "../../IconInfos/IconInfos";
import XIcon from "@static/icons/X.svg";
import GroupIcon from "@static/icons/Group.svg";
import { ICart } from "@features/cart/ICart";
import { IEirSalesAscertain } from "@modules/eirSalesFacade/interfaces/models/IEirSalesAscertain";
import { useBroadbandProviderQuestion } from "./useBroadbandProviderQuestion";
import { EYesNoNoneValues } from "@interface/EYesNoNoneValues";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";
import { cls } from "itsf-ui-common";
import type { Dispatch, SetStateAction } from "react";

interface IParams {
    offerId: number;
    onAnswerChange?: () => void;
    setCart: Dispatch<SetStateAction<ICart | undefined>>;
    ascertain?: IEirSalesAscertain;
    isAscertainLoading?: boolean;
    shouldForceValidate?: boolean;
    radioValue?: EYesNoNoneValues;
    setRadioValue?: Dispatch<SetStateAction<EYesNoNoneValues>>;
    onAgreeConfirmed?(): void;
}

export const BroadbandProviderQuestion = (props: IParams) => {
    const {
        offerId,
        setCart,
        ascertain,
        isAscertainLoading,
        shouldForceValidate,
        radioValue,
        setRadioValue,
        onAgreeConfirmed,
    } = props;
    const { t } = useTranslation("cart");
    const { classes } = useStyle();

    const {
        radioValue: currentRadioValue,
        isAccordionOpen,
        isLoading,
        handleRadioChange,
        handleAgreeAndContinue,
        getValidationErrorMessage,
    } = useBroadbandProviderQuestion({
        offerId,
        setCart,
        ascertain,
        isAscertainLoading,
        onValidationChange: () => undefined,
        radioValue,
        setRadioValue,
        onAgreeConfirmed,
    });

    const validationErrorMessage = shouldForceValidate ? getValidationErrorMessage() : undefined;
    const hasRadioError = validationErrorMessage === t("broadband.noOptionSelectedValidationError");
    const hasAgreeError = validationErrorMessage === t("broadband.agreeValidationError");

    return (
        <>
            <div
                className={cls(
                    classes.questionContainer,
                    isAccordionOpen ? classes.questionContainerCollapseOpen : undefined
                )}
            >
                <Typography className={classes.question} variant="normal">
                    {t("broadband.question")}
                </Typography>
                <FormControl component="fieldset" error={Boolean(validationErrorMessage)}>
                    <RadioGroup
                        className={classes.radioGroup}
                        row
                        value={currentRadioValue}
                        onChange={handleRadioChange}
                    >
                        <FormControlLabel
                            aria-label={t("broadband.yes")}
                            className={classes.radioLabel}
                            control={
                                <Radio
                                    className={cls(classes.radio, hasRadioError ? classes.radioError : undefined)}
                                    disabled={isLoading}
                                />
                            }
                            label={t("broadband.yes")}
                            value={EYesNoNoneValues.YES}
                        />
                        <FormControlLabel
                            aria-label={t("broadband.no")}
                            className={classes.radioLabel}
                            control={
                                <Radio
                                    className={cls(classes.radio, hasRadioError ? classes.radioError : undefined)}
                                    disabled={isLoading}
                                />
                            }
                            label={t("broadband.no")}
                            value={EYesNoNoneValues.NO}
                        />
                    </RadioGroup>
                </FormControl>
            </div>
            <Collapse in={isAccordionOpen} timeout={300}>
                <Stack className={classes.accordionContent} spacing={2}>
                    <Typography className={classes.warningText} variant="large">
                        {t("broadband.warning")}
                    </Typography>
                    <Typography className={classes.instructionText} variant="normal">
                        {t("broadband.instruction")}
                    </Typography>
                    <Stack spacing={2}>
                        <IconInfos icon={<XIcon />} text={t("broadband.cancel.yourself")} />
                        <IconInfos icon={<GroupIcon />} text={t("broadband.cancel.forYou")} />
                    </Stack>
                    <Stack
                        alignItems={{ xs: "stretch", lg: "center" }}
                        className={classes.disclaimerStack}
                        direction={{ xs: "column", lg: "row" }}
                        spacing={{ xs: 1.5, lg: 1 }}
                    >
                        <Typography className={classes.disclaimerText} variant="small">
                            {t("broadband.disclaimer")}
                        </Typography>
                        <div className={classes.buttonContainer}>
                            <LabeledButton
                                className={cls(
                                    classes.agreeButton,
                                    hasAgreeError ? classes.agreeButtonError : undefined
                                )}
                                disabled={isLoading}
                                labelText={t("broadband.agreeContinue")}
                                onClick={handleAgreeAndContinue}
                            >
                                {t("broadband.agreeContinue")}
                            </LabeledButton>
                        </div>
                    </Stack>
                </Stack>
            </Collapse>
        </>
    );
};
